/**
 * User State Slice
 *
 * Owns authentication state: the JWT token, the authenticated flag, the
 * photographer display name and the full `User` object (including role and
 * org/plan metadata).
 *
 * Cross-device correctness notes:
 *  - The token is mirrored into `safeStorage` under BOTH legacy keys
 *    ('auth_token' and 'token') because different parts of the API layer read
 *    different keys. Keep both writes in sync.
 *  - `login` seeds org/plan/company-logo state directly from the login
 *    response so a fresh device gets correct branding/role without a second
 *    round-trip.
 *  - `safeStorage` is used (not raw localStorage) for iOS Safari Private Mode
 *    compatibility — it transparently falls back to in-memory storage.
 */

import { setApiToken } from '../services/api/apiConfig.js';
import { logger } from '../utils/logger';
import { useCameraStore } from './cameraStore';
import { UserState, UserActions } from '@/types/store';
import { User } from '@/types/index';
import { safeStorage } from '../utils/safeStorage';

export const userSlice: UserState = {
  token: null,
  isAuthenticated: false,
  photographer: '',
  user: null, // Store full user object including role
};

export const userActions: UserActions = (set, get) => ({
  setToken: (token: string | null): void => {
    logger.debug('Setting API token');
    set({ token });
    setApiToken(token);

    // Also store in safeStorage for API service (iOS Private Mode compatible).
    // Both 'auth_token' and 'token' are written because different consumers in
    // the API layer historically read different keys — keep them in lockstep.
    if (token) {
      safeStorage.setItem('auth_token', token);
      safeStorage.setItem('token', token);
    } else {
      safeStorage.removeItem('auth_token');
      safeStorage.removeItem('token');
    }
  },

  login: (photographer: string, token: string, userData: User | null = null): void => {
    logger.info('User login successful', {
      photographer,
      role: userData?.role || 'unknown',
      userId: userData?.id,
    });

    // Fall back to a minimal user object when the caller only has a name.
    const user: User = userData || ({ username: photographer } as User);

    set({
      isAuthenticated: true,
      photographer,
      user,
    });

    get().setToken(token);

    // Seed org state from login response data (avoids a separate round-trip fetch)
    if (user.org_id) {
      get().setOrgRole(user.org_role ?? null);
      get().setPlanTier(user.plan_tier ?? 'free');
    } else {
      get().clearOrg();
    }

    // Seed the per-user company logo from the server so it works on a new device.
    // Only apply when the server returned a non-null value; if the server has no
    // logo yet, keep any existing localStorage logo (it will be synced on next save).
    if (user.company_logo) {
      get().setCompanyLogoLocal(user.company_logo);
    }

    // Save user data to storage for persistence (iOS Private Mode compatible)
    if (user) {
      try {
        safeStorage.setItem('user_data', JSON.stringify(user));
        logger.debug('User data saved to storage', { userId: user.id });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error('Failed to save user data to storage', { error: message });
      }
    }
  },

  logout: (): void => {
    logger.info('User logout');
    set({
      isAuthenticated: false,
      photographer: '',
      token: null,
      user: null,
    });
    get().setToken(null);
    get().clearOrg();

    // Fully release the camera. BackgroundCamera no longer stops tracks on
    // unmount (it keeps the stream alive across screen changes to avoid iOS
    // re-prompting), so logout is now the single place that tears it down.
    try {
      useCameraStore.getState().stopCamera();
    } catch (error: unknown) {
      logger.warn('Failed to stop camera on logout', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Clear the per-user company logo so the next user on this device
    // doesn't briefly see the previous user's branding before their own loads.
    get().setCompanyLogoLocal(null);

    // Clear all token storage (iOS Private Mode compatible)
    safeStorage.removeItem('auth_token');
    safeStorage.removeItem('token');
    safeStorage.removeItem('user_data');
  },

  updateUser: (userData: User): void => {
    logger.debug('Updating user data', { userId: userData?.id, role: userData?.role });
    set({ user: userData });
  },

  setIsAuthenticated: (isAuthenticated: boolean): void => {
    logger.debug('Setting authentication status', { isAuthenticated });
    set({ isAuthenticated });
  },

  setUser: (userData: User | null): void => {
    logger.debug('Setting user data', { userId: userData?.id, role: userData?.role });
    set({ user: userData });
  },

  // Check if current user is admin
  isAdmin: (): boolean => {
    const state = get();
    return state.user?.role === 'admin';
  },

  // Check if current user has specific role
  hasRole: (role: string): boolean => {
    const state = get();
    return state.user?.role === role;
  },

  // Get current user role
  getUserRole: (): string => {
    const state = get();
    return state.user?.role || 'user';
  },
});
